(() => {
  if (window.__BHD_WISHLIST_V1__) return;
  window.__BHD_WISHLIST_V1__ = true;

  const STORAGE_KEY = 'bhdWishlistV1';
  const HEART_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z"/></svg>';
  const PRODUCT_PATH_RE = /\/products\/([^/?#]+)/i;
  let toastTimer;
  let observerTimer;
  let wishlistRenderToken = 0;

  const root = () => {
    const value = window.Shopify?.routes?.root || '/';
    return value.endsWith('/') ? value : `${value}/`;
  };

  const route = (path) => `${root()}${String(path).replace(/^\//, '')}`;
  const wishlistUrl = () => route('pages/wishlist');
  const isSpanish = () => (document.documentElement.lang || '').toLowerCase().startsWith('es');

  const copy = () => isSpanish() ? {
    wishlist: 'LISTA DE DESEOS',
    save: 'Guardar en lista de deseos',
    remove: 'Quitar de lista de deseos',
    saved: 'GUARDADO',
    view: 'VER LISTA',
    removed: 'QUITADO DE TU LISTA',
    savedOne: '1 ARTÍCULO GUARDADO',
    savedMany: (n) => `${n} ARTÍCULOS GUARDADOS`,
    savedSoldOut: 'GUARDADO · AGOTADO POR AHORA',
    ready15: (n) => `${n} GUARDADOS · BUNDLE DE 15% LISTO`,
    ready20: (n) => `${n} GUARDADOS · BUNDLE DE 20% LISTO`,
    ready25: (n) => `${n} GUARDADOS · BUNDLE DE 25% LISTO`,
    add: 'AGREGAR AL CARRITO',
    soldOut: 'AGOTADO',
    shareTitle: 'COMPARTE TU LISTA',
    shareBody: 'Envía un carrito listo para comprar con todos los artículos disponibles que guardaste.',
    text: 'ENVIAR POR TEXTO',
    copyLink: 'COPIAR LINK DEL CARRITO',
    more: 'MÁS OPCIONES',
    close: 'Cerrar',
    copied: 'LINK DEL CARRITO COPIADO',
    noAvailable: 'NO HAY ARTÍCULOS DISPONIBLES PARA COMPARTIR',
    addAllDone: 'ARTÍCULOS AGREGADOS AL CARRITO',
    addError: 'NO SE PUDO AGREGAR AL CARRITO',
    shareMessage: 'Estos son los artículos de Barrio Hat Drop que quiero 👀'
  } : {
    wishlist: 'WISHLIST',
    save: 'Save to wishlist',
    remove: 'Remove from wishlist',
    saved: 'SAVED',
    view: 'VIEW WISHLIST',
    removed: 'REMOVED FROM YOUR WISHLIST',
    savedOne: '1 ITEM SAVED',
    savedMany: (n) => `${n} ITEMS SAVED`,
    savedSoldOut: 'SAVED · CURRENTLY SOLD OUT',
    ready15: (n) => `${n} SAVED · 15% BUNDLE READY`,
    ready20: (n) => `${n} SAVED · 20% BUNDLE READY`,
    ready25: (n) => `${n} SAVED · 25% BUNDLE READY`,
    add: 'ADD TO CART',
    soldOut: 'SOLD OUT',
    shareTitle: 'SHARE YOUR WISHLIST',
    shareBody: 'Send a ready-to-buy cart with every available item you saved.',
    text: 'TEXT WISHLIST',
    copyLink: 'COPY CART LINK',
    more: 'MORE SHARE OPTIONS',
    close: 'Close',
    copied: 'CART LINK COPIED',
    noAvailable: 'NO AVAILABLE ITEMS TO SHARE YET',
    addAllDone: 'ITEMS ADDED TO CART',
    addError: 'COULD NOT ADD ITEMS TO CART',
    shareMessage: 'These are the Barrio Hat Drop pieces I want 👀'
  };

  const normalizeImage = (src) => {
    if (!src) return '';
    if (src.startsWith('//')) return `https:${src}`;
    try { return new URL(src, window.location.origin).toString(); }
    catch (_) { return src; }
  };

  const escapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const handleFromUrl = (url) => {
    if (!url) return '';
    try {
      const parsed = new URL(url, window.location.origin);
      return decodeURIComponent(parsed.pathname.match(PRODUCT_PATH_RE)?.[1] || '');
    } catch (_) {
      return decodeURIComponent(String(url).match(PRODUCT_PATH_RE)?.[1] || '');
    }
  };

  const readItems = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter(item => item && item.handle) : [];
    } catch (_) {
      return [];
    }
  };

  const persistItems = (items, { render = true } = {}) => {
    const unique = [];
    const seen = new Set();
    items.forEach(item => {
      const key = String(item.handle || item.id || '');
      if (!key || seen.has(key)) return;
      seen.add(key);
      unique.push(item);
    });

    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(unique)); } catch (_) {}
    syncCounts(unique.length);
    syncButtons(unique);
    if (render) renderWishlistPage();
    document.dispatchEvent(new CustomEvent('bhd:wishlist:change', { detail: { items: unique } }));
    return unique;
  };

  const hasHandle = (items, handle) => items.some(item => item.handle === handle);

  const currencyCode = () => window.Shopify?.currency?.active || 'USD';
  const formatMoney = (cents) => {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currencyCode(),
        minimumFractionDigits: 2
      }).format(amount);
    } catch (_) {
      return `$${amount.toFixed(2)} USD`;
    }
  };

  const fetchProduct = async (handle) => {
    const response = await fetch(route(`products/${encodeURIComponent(handle)}.js`), {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    });
    if (!response.ok) throw new Error(`Wishlist product fetch failed: ${response.status}`);
    return response.json();
  };

  const itemFromProduct = (product, preferredVariantId) => {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const preferred = variants.find(v => String(v.id) === String(preferredVariantId || ''));
    const selectedVariant = preferred || variants.find(v => v.available) || variants[0] || null;
    const image = selectedVariant?.featured_image?.src || product.featured_image || product.images?.[0] || '';
    const baseUrl = product.url || route(`products/${product.handle}`);
    const productUrl = selectedVariant?.id ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}variant=${selectedVariant.id}` : baseUrl;

    return {
      id: String(product.id || ''),
      handle: product.handle,
      title: product.title || '',
      vendor: product.vendor || 'BARRIO HAT DROP',
      url: productUrl,
      image: normalizeImage(image),
      price: Number(selectedVariant?.price ?? product.price ?? 0),
      variantId: selectedVariant?.id ? String(selectedVariant.id) : '',
      variantTitle: selectedVariant?.public_title || selectedVariant?.title || '',
      available: Boolean(product.available),
      variantAvailable: Boolean(selectedVariant?.available),
      addedAt: Date.now()
    };
  };

  const preferredVariantFromButton = (button) => {
    const container = button.closest('.product__info-container, quick-view, .product');
    const input = container?.querySelector('form[action*="/cart/add"] [name="id"], product-form [name="id"], [name="id"][type="hidden"]');
    return input?.value || '';
  };

  const milestoneMessage = (count) => {
    const t = copy();
    if (count >= 8) return t.ready25(count);
    if (count >= 4) return t.ready20(count);
    if (count >= 2) return t.ready15(count);
    return count === 1 ? t.savedOne : t.savedMany(count);
  };

  const ensureToast = () => {
    let toast = document.getElementById('BHDWishlistToast');
    if (toast) return toast;
    toast = document.createElement('div');
    toast.id = 'BHDWishlistToast';
    toast.className = 'bhd-wishlist-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = `<span class="bhd-wishlist-toast__heart">${HEART_SVG}</span><span class="bhd-wishlist-toast__message"></span><a class="bhd-wishlist-toast__link" href="${wishlistUrl()}"></a>`;
    document.body.appendChild(toast);
    return toast;
  };

  const showToast = (message, { showLink = true } = {}) => {
    const t = copy();
    const toast = ensureToast();
    toast.querySelector('.bhd-wishlist-toast__message').textContent = message;
    const link = toast.querySelector('.bhd-wishlist-toast__link');
    link.textContent = t.view;
    link.hidden = !showLink;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 3200);
  };

  const setButtonState = (button, saved) => {
    const t = copy();
    button.classList.toggle('is-saved', saved);
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    button.setAttribute('aria-label', saved ? t.remove : t.save);
    button.setAttribute('title', saved ? t.remove : t.save);
    const label = button.querySelector('[data-bhd-wishlist-label]');
    if (label) label.textContent = saved ? t.saved : t.wishlist;
  };

  const syncButtons = (items = readItems()) => {
    document.querySelectorAll('[data-bhd-wishlist-toggle][data-bhd-handle]').forEach(button => {
      setButtonState(button, hasHandle(items, button.dataset.bhdHandle));
    });
  };

  const syncCounts = (count = readItems().length) => {
    document.querySelectorAll('[data-bhd-wishlist-count]').forEach(node => {
      node.textContent = count > 99 ? '99+' : String(count);
      node.toggleAttribute('hidden', count < 1);
      node.setAttribute('aria-label', isSpanish() ? `${count} artículos en la lista de deseos` : `${count} wishlist items`);
      node.closest('.bhd-wishlist-header-link, .bhd-wishlist-drawer-link')?.classList.toggle('is-populated', count > 0);
    });
  };

  const toggleWishlist = async (button) => {
    if (button.dataset.bhdBusy === 'true') return;
    const handle = button.dataset.bhdHandle;
    if (!handle) return;

    const items = readItems();
    const existing = items.find(item => item.handle === handle);
    button.dataset.bhdBusy = 'true';
    button.classList.add('is-bumping');
    setTimeout(() => button.classList.remove('is-bumping'), 440);

    if (existing) {
      persistItems(items.filter(item => item.handle !== handle));
      setButtonState(button, false);
      showToast(copy().removed);
      button.dataset.bhdBusy = 'false';
      return;
    }

    try {
      const product = await fetchProduct(handle);
      const item = itemFromProduct(product, preferredVariantFromButton(button));
      persistItems([item, ...items.filter(saved => saved.handle !== handle)]);
      setButtonState(button, true);
      const availableSaved = readItems().filter(saved => saved.variantAvailable).length;
      showToast(item.variantAvailable ? milestoneMessage(availableSaved) : copy().savedSoldOut);
    } catch (error) {
      console.error(error);
      showToast(isSpanish() ? 'NO SE PUDO GUARDAR. INTENTA DE NUEVO.' : 'COULD NOT SAVE. TRY AGAIN.', { showLink: false });
    } finally {
      button.dataset.bhdBusy = 'false';
    }
  };

  const createToggleButton = (handle, kind = 'card') => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = kind === 'product' ? 'bhd-wishlist-product-button' : 'bhd-wishlist-card-button';
    button.dataset.bhdWishlistToggle = '';
    button.dataset.bhdHandle = handle;
    button.setAttribute('aria-pressed', 'false');
    button.innerHTML = kind === 'product'
      ? `${HEART_SVG}<span data-bhd-wishlist-label>${copy().wishlist}</span>`
      : HEART_SVG;
    setButtonState(button, hasHandle(readItems(), handle));
    return button;
  };

  const initProductCards = (scope = document) => {
    scope.querySelectorAll('.card-wrapper').forEach(wrapper => {
      if (wrapper.querySelector(':scope > .bhd-wishlist-card-button')) return;
      const link = wrapper.querySelector('a.full-unstyled-link[href*="/products/"], a.card__media[href*="/products/"], a[href*="/products/"]');
      const handle = handleFromUrl(link?.getAttribute('href'));
      if (!handle) return;
      wrapper.classList.add('bhd-wishlist-card-ready');
      wrapper.appendChild(createToggleButton(handle, 'card'));
    });
  };

  const productUrlForContainer = (container) => {
    const quickView = container.closest('quick-view[data-product-url]');
    if (quickView?.dataset.productUrl) return quickView.dataset.productUrl;
    const quickAncestor = container.closest('[data-product-url]');
    if (quickAncestor?.dataset.productUrl) return quickAncestor.dataset.productUrl;
    return PRODUCT_PATH_RE.test(window.location.pathname) ? window.location.pathname : '';
  };

  const initProductButtons = (scope = document) => {
    scope.querySelectorAll('.product__info-container').forEach(container => {
      const title = container.querySelector('.product__title');
      if (!title || title.querySelector('.bhd-wishlist-product-button')) return;
      const handle = handleFromUrl(productUrlForContainer(container));
      if (!handle) return;
      title.classList.add('bhd-wishlist-title-ready');
      title.appendChild(createToggleButton(handle, 'product'));
    });
  };

  const injectDesktopHeaderLink = () => {
    if (document.querySelector('.bhd-wishlist-header-link')) return;
    const right = document.querySelector('.header__right');
    if (!right) return;

    const link = document.createElement('a');
    link.href = wishlistUrl();
    link.className = 'header__icon bhd-wishlist-header-link';
    link.setAttribute('aria-label', copy().wishlist);
    link.innerHTML = `${HEART_SVG}<span class="bhd-wishlist-count" data-bhd-wishlist-count hidden>0</span>`;

    const cartChild = Array.from(right.children).find(child => child.matches?.('cart-drawer') || child.querySelector?.('.header__icon--cart'));
    right.insertBefore(link, cartChild || null);
  };

  const injectMobileDrawerLink = () => {
    if (document.querySelector('.bhd-wishlist-drawer-link')) return;
    const container = document.querySelector('.menu-drawer__navigation-container');
    if (!container) return;

    const holder = document.createElement('div');
    holder.className = 'bhd-wishlist-drawer-link';
    holder.innerHTML = `<a href="${wishlistUrl()}" class="bhd-wishlist-drawer-link__anchor"><span class="bhd-wishlist-drawer-link__main">${HEART_SVG}<span>${copy().wishlist}</span></span><span class="bhd-wishlist-drawer-count" data-bhd-wishlist-count hidden>0</span></a>`;
    const utility = container.querySelector('.menu-drawer__utility-links, .menu-drawer__localization');
    container.insertBefore(holder, utility || null);
  };

  const bundleStatus = (count) => {
    if (isSpanish()) {
      if (count >= 8) return `${count} GUARDADOS · LISTOS PARA 25% DE DESCUENTO EN EL CARRITO`;
      if (count >= 4) return `${count} GUARDADOS · LISTOS PARA 20% DE DESCUENTO EN EL CARRITO`;
      if (count >= 2) return `${count} GUARDADOS · LISTOS PARA 15% DE DESCUENTO EN EL CARRITO`;
      if (count === 1) return 'GUARDA 1 MÁS PARA ARMAR UN BUNDLE DE 15%';
      return 'GUARDA 2 ARTÍCULOS PARA ARMAR UN BUNDLE DE 15%';
    }
    if (count >= 8) return `${count} SAVED · READY FOR 25% OFF IN CART`;
    if (count >= 4) return `${count} SAVED · READY FOR 20% OFF IN CART`;
    if (count >= 2) return `${count} SAVED · READY FOR 15% OFF IN CART`;
    if (count === 1) return 'SAVE 1 MORE ITEM TO BUILD A 15% BUNDLE';
    return 'SAVE 2 ITEMS TO BUILD A 15% BUNDLE';
  };

  const refreshSavedItems = async (items) => {
    return Promise.all(items.map(async saved => {
      try {
        const product = await fetchProduct(saved.handle);
        const refreshed = itemFromProduct(product, saved.variantId);
        refreshed.addedAt = saved.addedAt || Date.now();
        return refreshed;
      } catch (_) {
        return { ...saved, available: false, variantAvailable: false };
      }
    }));
  };

  const renderWishlistCards = (page, items) => {
    const grid = page.querySelector('[data-bhd-wishlist-grid]');
    const empty = page.querySelector('[data-bhd-wishlist-empty]');
    const countLabel = page.querySelector('[data-bhd-wishlist-page-count]');
    const bundleLabel = page.querySelector('[data-bhd-wishlist-bundle-status]');
    const addAll = page.querySelector('[data-bhd-wishlist-add-all]');
    const share = page.querySelector('[data-bhd-wishlist-share]');

    countLabel.textContent = isSpanish()
      ? `${items.length} ${items.length === 1 ? 'artículo guardado' : 'artículos guardados'}`
      : `${items.length} ${items.length === 1 ? 'saved item' : 'saved items'}`;
    const availableCount = items.filter(item => item.variantAvailable).length;
    bundleLabel.textContent = bundleStatus(availableCount);
    empty.hidden = items.length > 0;
    grid.hidden = items.length === 0;
    addAll.disabled = availableCount === 0;
    share.disabled = availableCount === 0;

    grid.innerHTML = items.map(item => {
      const productUrl = item.url || route(`products/${item.handle}`);
      const unavailable = !item.variantAvailable;
      return `
        <article class="bhd-wishlist-item" data-bhd-wishlist-item="${escapeHtml(item.handle)}">
          <div class="bhd-wishlist-item__media">
            <a href="${escapeHtml(productUrl)}" aria-label="${escapeHtml(item.title)}">
              ${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy" width="720" height="720">` : '<span class="bhd-wishlist-item__placeholder"></span>'}
            </a>
            <button type="button" class="bhd-wishlist-item__remove" data-bhd-wishlist-remove="${escapeHtml(item.handle)}" aria-label="${escapeHtml(copy().remove)}">${HEART_SVG}</button>
            ${unavailable ? `<span class="bhd-wishlist-item__soldout">${escapeHtml(copy().soldOut)}</span>` : ''}
          </div>
          <div class="bhd-wishlist-item__body">
            <p class="bhd-wishlist-item__vendor">${escapeHtml(item.vendor || 'BARRIO HAT DROP')}</p>
            <h2 class="bhd-wishlist-item__title"><a href="${escapeHtml(productUrl)}">${escapeHtml(item.title)}</a></h2>
            ${item.variantTitle && item.variantTitle !== 'Default Title' ? `<p class="bhd-wishlist-item__variant">${escapeHtml(item.variantTitle)}</p>` : ''}
            <p class="bhd-wishlist-item__price">${escapeHtml(formatMoney(item.price))}</p>
            <button type="button" class="bhd-wishlist-item__add" data-bhd-wishlist-add="${escapeHtml(item.handle)}" ${unavailable ? 'disabled' : ''}>${escapeHtml(unavailable ? copy().soldOut : copy().add)}</button>
          </div>
        </article>`;
    }).join('');
  };

  const renderWishlistPage = async () => {
    const page = document.querySelector('[data-bhd-wishlist-page]');
    if (!page) return;
    const token = ++wishlistRenderToken;
    const saved = readItems();
    renderWishlistCards(page, saved);
    page.classList.add('is-refreshing');

    if (!saved.length) {
      page.classList.remove('is-refreshing');
      return;
    }

    const refreshed = await refreshSavedItems(saved);
    if (token !== wishlistRenderToken) return;
    persistItems(refreshed, { render: false });
    renderWishlistCards(page, refreshed);
    page.classList.remove('is-refreshing');
  };

  const cartLinesFromWishlist = async () => {
    const refreshed = await refreshSavedItems(readItems());
    persistItems(refreshed, { render: false });
    return refreshed
      .filter(item => item.variantAvailable && item.variantId)
      .map(item => ({ id: Number(item.variantId), quantity: 1 }));
  };

  const cartPermalink = (lines) => {
    if (!lines.length) return '';
    const encodedLines = lines.map(line => `${line.id}:${line.quantity}`).join(',');
    const url = new URL(route(`cart/${encodedLines}`), window.location.origin);
    url.searchParams.set('utm_source', 'wishlist_share');
    url.searchParams.set('utm_medium', 'share');
    url.searchParams.set('utm_campaign', 'barrio_wishlist');
    return url.toString();
  };

  const addLinesToCart = async (lines, { renderDrawer = false } = {}) => {
    const miniCart = document.querySelector('mini-cart');
    const payload = { items: lines };

    if (miniCart?.getSectionsToRender) {
      payload.sections = miniCart.getSectionsToRender().map(section => section.id);
      payload.sections_url = window.location.pathname;
    }

    const response = await fetch(route('cart/add.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    });
    const parsedState = await response.json();
    if (!response.ok || parsedState.status) throw new Error(parsedState.description || parsedState.message || `Cart add failed: ${response.status}`);

    if (renderDrawer && miniCart?.renderContents && parsedState.sections) {
      await miniCart.renderContents(parsedState);
    }

    document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsedState } }));
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'wishlist' } }));
    return parsedState;
  };

  const ensureShareSheet = () => {
    let sheet = document.getElementById('BHDWishlistShareSheet');
    if (sheet) return sheet;
    const t = copy();
    sheet = document.createElement('div');
    sheet.id = 'BHDWishlistShareSheet';
    sheet.className = 'bhd-wishlist-share-sheet';
    sheet.hidden = true;
    sheet.innerHTML = `
      <button class="bhd-wishlist-share-sheet__backdrop" type="button" data-bhd-wishlist-share-close aria-label="${escapeHtml(t.close)}"></button>
      <section class="bhd-wishlist-share-sheet__panel" role="dialog" aria-modal="true" aria-labelledby="BHDWishlistShareTitle">
        <button type="button" class="bhd-wishlist-share-sheet__close" data-bhd-wishlist-share-close aria-label="${escapeHtml(t.close)}">×</button>
        <p class="bhd-wishlist-share-sheet__eyebrow">BARRIO HAT DROP</p>
        <h2 id="BHDWishlistShareTitle">${escapeHtml(t.shareTitle)}</h2>
        <p>${escapeHtml(t.shareBody)}</p>
        <div class="bhd-wishlist-share-sheet__actions">
          <button type="button" data-bhd-wishlist-share-text>${escapeHtml(t.text)}</button>
          <button type="button" data-bhd-wishlist-share-copy>${escapeHtml(t.copyLink)}</button>
          <button type="button" data-bhd-wishlist-share-native ${navigator.share ? '' : 'hidden'}>${escapeHtml(t.more)}</button>
        </div>
      </section>`;
    document.body.appendChild(sheet);
    return sheet;
  };

  const openShareSheet = async () => {
    const lines = await cartLinesFromWishlist();
    if (!lines.length) {
      showToast(copy().noAvailable, { showLink: false });
      return;
    }
    const sheet = ensureShareSheet();
    sheet.dataset.cartLink = cartPermalink(lines);
    sheet.hidden = false;
    requestAnimationFrame(() => sheet.classList.add('is-open'));
    document.body.classList.add('bhd-wishlist-share-open');
    sheet.querySelector('[data-bhd-wishlist-share-text]')?.focus();
  };

  const closeShareSheet = () => {
    const sheet = document.getElementById('BHDWishlistShareSheet');
    if (!sheet) return;
    sheet.classList.remove('is-open');
    document.body.classList.remove('bhd-wishlist-share-open');
    setTimeout(() => { sheet.hidden = true; }, 240);
  };

  const shareMessage = (link) => `${copy().shareMessage}\n${link}`;

  const initAll = (scope = document) => {
    initProductCards(scope);
    initProductButtons(scope);
    injectDesktopHeaderLink();
    injectMobileDrawerLink();
    syncCounts();
    syncButtons();
    const page = document.querySelector('[data-bhd-wishlist-page]');
    if (page && page.dataset.bhdWishlistInitialized !== 'true') {
      page.dataset.bhdWishlistInitialized = 'true';
      renderWishlistPage();
    }
  };

  document.addEventListener('click', async (event) => {
    const toggle = event.target.closest('[data-bhd-wishlist-toggle]');
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();
      await toggleWishlist(toggle);
      return;
    }

    const remove = event.target.closest('[data-bhd-wishlist-remove]');
    if (remove) {
      event.preventDefault();
      const handle = remove.dataset.bhdWishlistRemove;
      persistItems(readItems().filter(item => item.handle !== handle));
      showToast(copy().removed);
      return;
    }

    const add = event.target.closest('[data-bhd-wishlist-add]');
    if (add) {
      event.preventDefault();
      const item = readItems().find(saved => saved.handle === add.dataset.bhdWishlistAdd);
      if (!item?.variantId || !item.variantAvailable) return;
      add.disabled = true;
      try {
        await addLinesToCart([{ id: Number(item.variantId), quantity: 1 }], { renderDrawer: true });
        showToast(copy().addAllDone, { showLink: false });
      } catch (error) {
        console.error(error);
        showToast(copy().addError, { showLink: false });
      } finally {
        add.disabled = false;
      }
      return;
    }

    if (event.target.closest('[data-bhd-wishlist-add-all]')) {
      event.preventDefault();
      const lines = await cartLinesFromWishlist();
      if (!lines.length) return;
      const button = event.target.closest('[data-bhd-wishlist-add-all]');
      button.disabled = true;
      try {
        await addLinesToCart(lines, { renderDrawer: false });
        window.location.href = route('cart');
      } catch (error) {
        console.error(error);
        button.disabled = false;
        showToast(copy().addError, { showLink: false });
      }
      return;
    }

    if (event.target.closest('[data-bhd-wishlist-share]')) {
      event.preventDefault();
      await openShareSheet();
      return;
    }

    if (event.target.closest('[data-bhd-wishlist-share-close]')) {
      event.preventDefault();
      closeShareSheet();
      return;
    }

    const sheet = document.getElementById('BHDWishlistShareSheet');
    if (sheet && !sheet.hidden) {
      const link = sheet.dataset.cartLink || '';
      if (event.target.closest('[data-bhd-wishlist-share-text]')) {
        event.preventDefault();
        const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        window.location.href = `sms:${isiOS ? '&' : '?'}body=${encodeURIComponent(shareMessage(link))}`;
        return;
      }
      if (event.target.closest('[data-bhd-wishlist-share-copy]')) {
        event.preventDefault();
        try {
          await navigator.clipboard.writeText(link);
          showToast(copy().copied, { showLink: false });
        } catch (_) {
          const input = document.createElement('textarea');
          input.value = link;
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          input.remove();
          showToast(copy().copied, { showLink: false });
        }
        closeShareSheet();
        return;
      }
      if (event.target.closest('[data-bhd-wishlist-share-native]')) {
        event.preventDefault();
        if (navigator.share) {
          try { await navigator.share({ title: copy().shareTitle, text: copy().shareMessage, url: link }); } catch (_) {}
        }
      }
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeShareSheet();
  });

  document.addEventListener('quickview:loaded', () => setTimeout(() => initAll(document), 0));
  document.addEventListener('shopify:section:load', (event) => initAll(event.target || document));
  document.addEventListener('bhd:wishlist:change', () => {
    syncCounts();
    syncButtons();
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    syncCounts();
    syncButtons();
    renderWishlistPage();
  });

  const observer = new MutationObserver((mutations) => {
    if (!mutations.some(mutation => mutation.addedNodes?.length)) return;
    clearTimeout(observerTimer);
    observerTimer = setTimeout(() => initAll(document), 80);
  });

  const boot = () => {
    initAll(document);
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
